import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputText as PrimereactInputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Slider } from 'primereact/slider';
import { VirtualScroller } from 'primereact/virtualscroller';
import { useTranslation } from 'react-i18next';

// @ts-ignore
import { ReactComponent as SortAscIcon } from '../../assets/images/sort-asc-icon.svg';
// @ts-ignore
import { ReactComponent as SortDescIcon } from '../../assets/images/sort-desc-icon.svg';
// @ts-ignore
import { ReactComponent as SearchIcon } from '../../assets/images/search-icon.svg';

import { FILTER_TYPES } from '../datatable2/constants/FilterTypes';
import type { FilterType } from '../datatable2/types/FilterType';

import Calendar from '../input/Calendar';
import InputText from '../input/InputText';
import InputAmount from '../input/InputAmount';

// @ts-ignore
import './FilterPanel.scss';

interface FilterPanelProps {
    ref: React.RefObject<any>;
    onSortAsc?: false | (() => void);
    onSortDesc?: false | (() => void);
    type?: FilterType;
    params?: any;
    applyFilter?: (type: FilterType, value: any) => void;
    timestamp?: Date;
}

const SLIDER_MAX_VALUE_DEFAULT = 1000000000;
const ITEM_SIZE = 38;
const MAX_VISIBLE_ITEMS = 5;

const getPanelValue = (params: any) => params?.value ?? params ?? {};

const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(value, max));

const FilterPanel: React.FC<FilterPanelProps> = ({
    ref,
    onSortAsc,
    onSortDesc,
    type,
    params,
    applyFilter,
    timestamp,
}) => {
    const { t } = useTranslation();

    const searchRef = useRef<HTMLInputElement | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [booleanValue, setBooleanValue] = useState<boolean | null>(null);
    const [list, setList] = useState<any[]>([]);
    const [textOptions, setTextOptions] = useState<any[]>([]);
    const [dateRange, setDateRange] = useState<any>({ from: null, to: null });

    const [numberRange, setNumberRange] = useState<any>({
        from: null,
        to: null,
        minValue: 0,
        maxValue: SLIDER_MAX_VALUE_DEFAULT,
    });

    const [amountRange, setAmountRange] = useState<any>({
        from: null,
        to: null,
        minValue: 0,
        maxValue: SLIDER_MAX_VALUE_DEFAULT,
    });

    useEffect(() => {
        if (type === FILTER_TYPES.LIST) {
            setList((params ?? []).map((item: any) => ({ ...item, hidden: false })));
            setTextOptions([]);
            setSearchQuery('');
            setDateRange({ from: null, to: null });
            return;
        }

        if (type === FILTER_TYPES.TEXT) {
            const selected = Array.isArray(params?.value) ? params.value : [];
            const options = params?.options ?? [];

            setList([]);
            setTextOptions(
                options.map((item: any) => ({
                    ...item,
                    checked: selected.some((selectedItem: any) => selectedItem.id === item.id),
                    hidden: false,
                })),
            );
            setSearchQuery(
                typeof params === 'string'
                    ? params
                    : Array.isArray(params?.value)
                        ? ''
                        : params?.value ?? '',
            );
            return;
        }

        if (type === FILTER_TYPES.BOOLEAN) {
            setBooleanValue(params !== undefined && params !== null ? params : null);
            setList([]);
            setTextOptions([]);
            setSearchQuery('');
            return;
        }

        if (type === FILTER_TYPES.DATE_RANGE || type === FILTER_TYPES.DATE_TIME_RANGE) {
            setList([]);
            setTextOptions([]);
            setSearchQuery('');
            setDateRange({
                from: params?.from ?? null,
                to: params?.to ?? null,
                minDate: params?.minDate ?? null,
                maxDate: params?.maxDate ?? null,
            });
            return;
        }

        if (type === FILTER_TYPES.NUMBER_RANGE) {
            const value = getPanelValue(params);

            setList([]);
            setTextOptions([]);
            setSearchQuery('');
            setDateRange({ from: null, to: null });
            setNumberRange({
                from: value?.from ?? null,
                to: value?.to ?? null,
                minValue: value?.minValue ?? 0,
                maxValue: value?.maxValue ?? SLIDER_MAX_VALUE_DEFAULT,
            });
            return;
        }

        if (type === FILTER_TYPES.AMOUNT) {
            const value = getPanelValue(params);

            setList([]);
            setTextOptions([]);
            setSearchQuery('');
            setDateRange({ from: null, to: null });
            setAmountRange({
                from: value?.from ?? null,
                to: value?.to ?? null,
                minValue: value?.minValue ?? 0,
                maxValue: value?.maxValue ?? SLIDER_MAX_VALUE_DEFAULT,
            });
        }
    }, [type, params, timestamp]);

    useEffect(() => {
        if (type !== FILTER_TYPES.LIST) return;

        setList(prev => {
            const query = searchQuery.trim().toUpperCase();

            return prev.map(item => ({
                ...item,
                hidden: query ? !item.label.toUpperCase().includes(query) : false,
            }));
        });
    }, [searchQuery, type]);

    useEffect(() => {
        if (type !== FILTER_TYPES.TEXT) return;

        setTextOptions(prev => {
            const query = searchQuery.trim().toUpperCase();

            return prev.map(item => ({
                ...item,
                hidden: query ? !item.label.toUpperCase().includes(query) : false,
            }));
        });
    }, [searchQuery, type]);

    const applyFilterImpl = () => {
        if (!applyFilter || !type) return;

        const selectedTextOptions = textOptions
            .filter(item => item.checked)
            .map(item => ({ id: item.id, label: item.label }));

        const value =
            type === FILTER_TYPES.LIST
                ? list
                    .filter(item => item.checked)
                    .map(item => ({ id: item.id, label: item.label }))
                : type === FILTER_TYPES.TEXT
                    ? selectedTextOptions.length > 0
                        ? selectedTextOptions
                        : searchQuery
                    : type === FILTER_TYPES.DATE_RANGE
                        ? dateRange
                        : type === FILTER_TYPES.DATE_TIME_RANGE
                            ? dateRange
                            : type === FILTER_TYPES.NUMBER_RANGE
                                ? { from: numberRange.from, to: numberRange.to }
                                : type === FILTER_TYPES.AMOUNT
                                    ? { from: amountRange.from, to: amountRange.to }
                                    : type === FILTER_TYPES.BOOLEAN
                                        ? booleanValue
                                        : null;

        applyFilter(type, value);
        ref.current?.hide();
    };

    const onShowPanel = () => {
        if (type === FILTER_TYPES.TEXT && textOptions.length === 0) {
            searchRef.current?.focus();
        }
    };

    const visibleList = list.filter(item => item.hidden !== true);
    const visibleTextOptions = textOptions.filter(item => item.hidden !== true);

    const listScrollerHeight = useMemo(
        () => Math.min(visibleList.length, MAX_VISIBLE_ITEMS) * ITEM_SIZE,
        [visibleList.length],
    );

    const textScrollerHeight = useMemo(
        () => Math.min(visibleTextOptions.length, MAX_VISIBLE_ITEMS) * ITEM_SIZE,
        [visibleTextOptions.length],
    );

    const renderRangeSlider = (
        range: any,
        setRange: React.Dispatch<React.SetStateAction<any>>,
        isAmount = false,
    ) => {
        const min = range.minValue ?? 0;
        const max = range.maxValue ?? SLIDER_MAX_VALUE_DEFAULT;

        const sliderValue: [number, number] = [
            range.from ?? min,
            range.to ?? max,
        ];

        const updateFrom = (rawValue: any) => {
            const parsed =
                rawValue === '' || rawValue === null || rawValue === undefined
                    ? null
                    : Number(rawValue);

            setRange((prev: any) => {
                const nextFrom = parsed === null ? null : clamp(parsed, min, max);
                const nextTo =
                    prev.to !== null && nextFrom !== null && nextFrom > prev.to
                        ? nextFrom
                        : prev.to;

                return { ...prev, from: nextFrom, to: nextTo };
            });
        };

        const updateTo = (rawValue: any) => {
            const parsed =
                rawValue === '' || rawValue === null || rawValue === undefined
                    ? null
                    : Number(rawValue);

            setRange((prev: any) => {
                const nextTo = parsed === null ? null : clamp(parsed, min, max);
                const nextFrom =
                    prev.from !== null && nextTo !== null && nextTo < prev.from
                        ? nextTo
                        : prev.from;

                return { ...prev, from: nextFrom, to: nextTo };
            });
        };

        return (
            <div className="w-full p-2">
                <div className="mb-3 flex gap-2">
                    {isAmount ? (
                        <InputAmount
                            id="amountRangeFrom"
                            inputClassName="w-full small p-inputtext p-component"
                            label={t('filterPanel.amountFrom')}
                            value={range.from}
                            maxValue={max}
                            validator={null}
                            validations={null}
                            onChange={(e: any) => updateFrom(e.target.value)}
                        />
                    ) : (
                        <InputText
                            id="numberRangeFrom"
                            keyfilter="pint"
                            inputClassName="small"
                            label={t('filterPanel.numberFrom')}
                            value={range.from ?? ''}
                            leftIcon={null}
                            validator={null}
                            validations={null}
                            onChange={(e: any) => updateFrom(e.target.value)}
                        />
                    )}

                    <div className="field">
                        {isAmount ? (
                            <InputAmount
                                id="amountRangeTo"
                                inputClassName="w-full small p-inputtext p-component"
                                label={t('filterPanel.amountTo')}
                                value={range.to}
                                maxValue={max}
                                validator={null}
                                validations={null}
                                onChange={(e: any) => updateTo(e.target.value)}
                            />
                        ) : (
                            <InputText
                                id="numberRangeTo"
                                keyfilter="pint"
                                inputClassName="small"
                                label={t('filterPanel.numberTo')}
                                value={range.to ?? ''}
                                leftIcon={null}
                                validator={null}
                                validations={null}
                                onChange={(e: any) => updateTo(e.target.value)}
                            />
                        )}
                    </div>
                </div>

                <div className="mb-3">
                    <Slider
                        value={sliderValue}
                        min={min}
                        max={max}
                        step={1}
                        range
                        className="w-full"
                        onChange={e => {
                            const [from, to] = e.value as number[];

                            setRange((prev: any) => ({
                                ...prev,
                                from,
                                to,
                            }));
                        }}
                    />
                </div>
            </div>
        );
    };

    const renderTextFilter = () => {
        if (textOptions.length === 0) {
            return (
                <div className="w-full p-2">
                    <span className="p-input-icon-left w-full">
                        <PrimereactInputText
                            value={searchQuery}
                            className="search-input small"
                            placeholder={t('filterPanel.search')}
                            onChange={e => setSearchQuery(e.target.value)}
                            ref={searchRef}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    applyFilterImpl();
                                }
                            }}
                        />

                        <SearchIcon />
                    </span>
                </div>
            );
        }

        return (
            <div className="w-full p-2">
                {textOptions.length > 5 && (
                    <span className="p-input-icon-left w-full">
                        <PrimereactInputText
                            value={searchQuery}
                            className="search-input small"
                            placeholder={t('filterPanel.search')}
                            onChange={e => setSearchQuery(e.target.value)}
                            ref={searchRef}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    applyFilterImpl();
                                }
                            }}
                        />

                        <SearchIcon />
                    </span>
                )}

                <div className="content-list">
                    {visibleTextOptions.length > 0 && (
                        <VirtualScroller
                            items={visibleTextOptions}
                            itemSize={ITEM_SIZE}
                            itemTemplate={item => (
                                <div
                                    className="item"
                                    key={`text-checkbox-${item.id}`}
                                    style={{ height: `${ITEM_SIZE}px` }}
                                >
                                    <Checkbox
                                        checked={item.checked}
                                        onChange={e => {
                                            setTextOptions(prev =>
                                                prev.map(option =>
                                                    option.id === item.id
                                                        ? { ...option, checked: e.checked }
                                                        : option,
                                                ),
                                            );
                                        }}
                                    />

                                    <div className="item-text">{item.label}</div>
                                </div>
                            )}
                            style={{
                                width: '100%',
                                height: `${textScrollerHeight}px`,
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    return (
        <OverlayPanel
            ref={ref}
            closeOnEscape
            dismissable
            unstyled
            className="filter-panel"
            onShow={onShowPanel}
        >
            <div className="filter-panel-content">
                {onSortAsc && onSortDesc && (
                    <>
                        <div className="filter-panel-sort p-2">
                            <div className="item" onClick={() => onSortAsc()}>
                                <SortAscIcon className="sort-icon" />
                                <div className="item-text">{t('filterPanel.sortAsc')}</div>
                            </div>

                            <div className="item" onClick={() => onSortDesc()}>
                                <SortDescIcon className="sort-icon" />
                                <div className="item-text">{t('filterPanel.sortDesc')}</div>
                            </div>
                        </div>

                        <div className="separator" />
                    </>
                )}

                {type === FILTER_TYPES.LIST && (
                    <div className="w-full p-2">
                        {list.length > 5 && (
                            <span className="p-input-icon-left w-full">
                                <PrimereactInputText
                                    value={searchQuery}
                                    className="search-input small"
                                    placeholder={t('filterPanel.search')}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />

                                <SearchIcon />
                            </span>
                        )}

                        <div className="content-list">
                            {visibleList.length > 0 && (
                                <VirtualScroller
                                    items={visibleList}
                                    itemSize={ITEM_SIZE}
                                    itemTemplate={item => (
                                        <div
                                            className="item"
                                            key={`checkbox-${item.id}`}
                                            style={{ height: `${ITEM_SIZE}px` }}
                                        >
                                            <Checkbox
                                                checked={item.checked}
                                                onChange={e => {
                                                    setList(prev =>
                                                        prev.map(i =>
                                                            i.id === item.id
                                                                ? { ...i, checked: e.checked }
                                                                : i,
                                                        ),
                                                    );
                                                }}
                                            />
                                            <div className="item-text">{item.label}</div>
                                        </div>
                                    )}
                                    style={{
                                        width: '100%',
                                        height: `${listScrollerHeight}px`,
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}

                {type === FILTER_TYPES.TEXT && renderTextFilter()}

                {type === FILTER_TYPES.DATE_RANGE && (
                    <div className="w-full p-2 flex gap-2">
                        <Calendar
                            id="dateRangeFrom"
                            value={dateRange.from}
                            inputClassName="small"
                            label={t('filterPanel.dateFrom')}
                            validator={null}
                            validations={null}
                            minDate={dateRange.minDate}
                            maxDate={dateRange.maxDate}
                            viewDate={dateRange.minDate}
                            onChange={(e: any) => {
                                setDateRange((prev: any) => ({
                                    ...prev,
                                    from: e.value,
                                }));
                            }}
                        />

                        <div className="field">
                            <Calendar
                                id="dateRangeTo"
                                value={dateRange.to}
                                inputClassName="small"
                                label={t('filterPanel.dateTo')}
                                validator={null}
                                validations={null}
                                minDate={dateRange.minDate}
                                maxDate={dateRange.maxDate}
                                viewDate={dateRange.maxDate}
                                onChange={(e: any) => {
                                    setDateRange((prev: any) => ({
                                        ...prev,
                                        to: e.value,
                                    }));
                                }}
                            />
                        </div>
                    </div>
                )}

                {type === FILTER_TYPES.DATE_TIME_RANGE && (
                    <div className="w-full p-2 flex gap-2">
                        <Calendar
                            id="dateTimeRangeFrom"
                            value={dateRange.from}
                            inputClassName="small"
                            label={t('filterPanel.dateFrom')}
                            validator={null}
                            validations={null}
                            showTime
                            onChange={(e: any) => {
                                setDateRange((prev: any) => ({
                                    ...prev,
                                    from: e.value,
                                }));
                            }}
                        />

                        <div className="field">
                            <Calendar
                                id="dateTimeRangeTo"
                                value={dateRange.to}
                                inputClassName="small"
                                label={t('filterPanel.dateTo')}
                                validator={null}
                                validations={null}
                                showTime
                                onChange={(e: any) => {
                                    setDateRange((prev: any) => ({
                                        ...prev,
                                        to: e.value,
                                    }));
                                }}
                            />
                        </div>
                    </div>
                )}

                {type === FILTER_TYPES.NUMBER_RANGE &&
                    renderRangeSlider(numberRange, setNumberRange, false)}

                {type === FILTER_TYPES.AMOUNT &&
                    renderRangeSlider(amountRange, setAmountRange, true)}

                {type === FILTER_TYPES.BOOLEAN && (
                    <div className="w-full p-2 flex flex-column gap-2">
                        <div className="flex align-items-center">
                            <Checkbox
                                inputId="boolean-true"
                                name="boolean-filter"
                                checked={booleanValue === true}
                                onChange={() => setBooleanValue(true)}
                            />
                            <label htmlFor="boolean-true" className="ml-2 cursor-pointer">
                                {t('Boolean.true')}
                            </label>
                        </div>

                        <div className="flex align-items-center">
                            <Checkbox
                                inputId="boolean-false"
                                name="boolean-filter"
                                checked={booleanValue === false}
                                onChange={() => setBooleanValue(false)}
                            />
                            <label htmlFor="boolean-false" className="ml-2 cursor-pointer">
                                {t('Boolean.false')}
                            </label>
                        </div>
                    </div>
                )}

                <Button
                    label={t('filterPanel.apply')}
                    className="small w-full"
                    onClick={applyFilterImpl}
                />
            </div>
        </OverlayPanel>
    );
};

export default FilterPanel;